import { ReactNode, useRef, useState } from "react";
import {
  Button,
  message,
  Flex,
  Typography,
  Alert,
  Card,
  Form,
  Row,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useParams, useNavigate } from "react-router";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { EventData } from "../../models/event";
import { transformImageFieldsToUIDs } from "../../utils/TransfromImageFieldsToUIDs";
import { updateCall } from "../../constants/apicalls.constant";
import { APP_TIMEZONE } from "../../utils/dayjsConfig";
import { setSelectedEventList } from "../../store/slices/event.slice";
import { ROUTES } from "../../constants/routes.constant";
import { checkForValidation } from "../../store/slices/validation.slice";
import { setEditEvent } from "../../store/slices/eventedit.slice";
import { isEventSetupReadOnly } from "../../utils/eventEditAccess";

import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import DateTimePicker from "../../components/DateTimePicker";
import {
  formatAllDatesForSubmit,
  convertToDayjsForForm,
} from "../../utils/DateAndTimeConvertor";
import {
  getPublishDateTimeBounds,
  publishMaxDateTimeError,
} from "../../utils/DateFn";
import EventSetupReviewSummary from "../../components/event/EventSetupReviewSummary";

const { Text, Paragraph } = Typography;

type PublishStatus = "draft" | "published" | "scheduled" | "requested";

const PUBLISH_SCHEDULE_FIELDS = [
  "to_publish_date",
  "to_publish_time",
  "_datetime_range_validator_to_publish_date_to_publish_time",
] as const;

const REVIEW_PAGE_CONTAINER = "p-[24px] max-w-4xl mx-auto";
const REVIEW_CARD_STYLE = { marginTop: 24, borderRadius: 12 };
const REVIEW_SUMMARY_SPACING = { marginTop: 24 };

type ReviewPageShellProps = {
  setupBackPath: string;
  loading?: boolean;
  title?: string;
  description?: string;
  backRoute?: string;
  children: ReactNode;
};

const ReviewPageShell = ({
  setupBackPath,
  loading = false,
  title = "Review & Publish",
  description,
  backRoute = setupBackPath,
  children,
}: ReviewPageShellProps) => (
  <Loader spinning={loading}>
    <div className={REVIEW_PAGE_CONTAINER}>
      <PageHeader
        title={title}
        description={description}
        backBtn
        backRoute={backRoute}
      />
      <Card style={REVIEW_CARD_STYLE}>{children}</Card>
    </div>
  </Loader>
);

const ReviewAndPublish = () => {
  const event = useSelector((state: RootState) => state.event);
  const geoTimezone = useSelector(
    (state: RootState) => state.geolocation.data.timezone,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState<PublishStatus>(
    event.status === "scheduled"
      ? "scheduled"
      : event.status === "approved" 
        ? "published"
        : "draft",
  );

  const [form] = Form.useForm();
  const submittingRef = useRef(false);

  const { loading, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  const isInReview = event.status === "requested";
  const isApproved = event?.status === "approved";
  const isRejected = event?.status === "rejected";
  const isReadOnlyPast = isEventSetupReadOnly(event);

  const setupBackPath = id
    ? ROUTES.ADD_EVENT_CUSTOM_FORM.path.replace(":id", id)
    : ROUTES.LIST_EVENT.path;

  const clearPublishScheduleFields = () => {
    form.setFieldsValue({
      to_publish_date: undefined,
      to_publish_time: undefined,
    });
    form.setFields(
      PUBLISH_SCHEDULE_FIELDS.map((name) => ({ name, errors: [] })),
    );
  };

  const selectPublishNow = () => {
    setStatus("published");
    clearPublishScheduleFields();
  };

  const selectSchedule = () => {
    setStatus("scheduled");
  };

  const handleUpdateSuccess = (data: EventData) => {
    dispatch(setSelectedEventList(data));

    if (data.status === "draft") {
      const wasLive = ["published", "scheduled", "approved"].includes(
        event.status || "",
      );
      message.success(
        wasLive ? "Event unpublished successfully." : "Event saved as draft.",
      );
    } else if (data.status === "requested") {
      message.success("Event submitted for admin approval.");
      navigate(ROUTES.LIST_EVENT.path);
    } else if (["published", "scheduled"].includes(data.status!)) {
      dispatch(checkForValidation({ name: "", check: false }));
      dispatch(setEditEvent({ editEvent: false }));
      message.success(
        data.status === "scheduled"
          ? "Event scheduled successfully!"
          : "Event published successfully!",
      );
      navigate(ROUTES.DASHBOARD.path.replace(":id", id!));
    }
  };

  const buildEventPayload = (
    nextStatus: PublishStatus,
    extra?: Partial<EventData>,
  ): EventData => {
    const eventWithTimezone = event as EventData & { timezone?: string };
    const valueToSend: EventData & { timezone?: string } = {
      ...event,
      status: nextStatus,
      timezone:
        eventWithTimezone.timezone ||
        geoTimezone ||
        localStorage.getItem("timezone") ||
        APP_TIMEZONE,
      ...extra,
    };

    return transformImageFieldsToUIDs(valueToSend);
  };

  const updateEvent = async (payload: EventData) => {
    if (!id || loading || submittingRef.current) return;

    submittingRef.current = true;
    try {
      const res = await trigger(updateCall<EventData>(id, payload));

      if (res?.status) {
        handleUpdateSuccess(res.data);
      } else {
        message.error(res?.message || "Something went wrong.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong.");
    } finally {
      submittingRef.current = false;
    }
  };

  const submitForReview = () => updateEvent(buildEventPayload("requested"));

  const unpublishEvent = () => {
    const { tickets: _tickets, ...payload } = buildEventPayload("draft");
    updateEvent(payload as EventData);
  };

  const canUnpublish =
    !event.has_registrations && event.status === "published";

  const onSubmit = async (values: EventData) => {
    const extra =
      status === "scheduled" ? formatAllDatesForSubmit(values) : undefined;

    await updateEvent(buildEventPayload(status, extra));
  };

  const submitPublishForm = async () => {
    try {
      if (status === "scheduled") {
        await form.validateFields([...PUBLISH_SCHEDULE_FIELDS]);
      }
      form.submit();
    } catch {
      message.error("Please select publish date and time to schedule.");
    }
  };

  const publishScheduleForm = (submitLabel: string) => (
    <div
      style={{
        marginTop: 24,
        paddingTop: 24,
        borderTop: "1px solid #f0f0f0",
      }}
    >
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        initialValues={
          event.to_publish_date || event.to_publish_time
            ? convertToDayjsForForm({
                to_publish_date: event.to_publish_date,
                to_publish_time: event.to_publish_time,
              })
            : undefined
        }
      >
        <Form.Item label="How would you like to publish?">
          <Flex gap={10} wrap="wrap">
            <Button
              onClick={selectPublishNow}
              type={status === "published" ? "primary" : "default"}
            >
              Publish Now
            </Button>
            <Button
              onClick={selectSchedule}
              type={status === "scheduled" ? "primary" : "default"}
            >
              {event.status === "scheduled" ? "Reschedule" : "Schedule"}
            </Button>
          </Flex>
        </Form.Item>

        {status === "scheduled" && (
          <Row gutter={[24, 0]}>
            <DateTimePicker
              form={form}
              label={["Publish Date", "Publish Time"]}
              name={["to_publish_date", "to_publish_time"]}
              disabledBasedOn={event?.start_date}
              bounds={getPublishDateTimeBounds({
                start_date: event.start_date,
                start_time: event.start_time,
              })}
              maxDateTimeErrorMessage={publishMaxDateTimeError}
            />
          </Row>
        )}
        <Flex
          gap={10}
          wrap="wrap"
          style={{ marginTop: 20, justifyContent: "flex-end" }}
        >
          <Button onClick={() => navigate(setupBackPath)}>Back</Button>
          <Button type="primary" loading={loading} onClick={submitPublishForm}>
            {submitLabel}
          </Button>
        </Flex>
      </Form>
    </div>
  );

  if (isReadOnlyPast) {
    const pastLabel = event.status === "archived" ? "archived" : "completed";

    return (
      <ReviewPageShell
        title="Event Details"
        description="Review your past event setup (read-only)"
        backRoute={ROUTES.LIST_EVENT.path}
        setupBackPath={setupBackPath}
      >
        <Alert
          message={`This event is ${pastLabel}`}
          description="Setup details are shown for reference only. Use Dashboard and other tabs for registrations and reports."
          type="info"
          showIcon
        />
        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>
      </ReviewPageShell>
    );
  }

  if (isInReview) {
    return (
      <ReviewPageShell setupBackPath={setupBackPath}>
        <Alert
          message="Your event is under review"
          description="We've received your submission. Our team will review it and get back to you shortly. You'll see the outcome reflected here."
          type="warning"
          showIcon
        />
        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>
      </ReviewPageShell>
    );
  }

  if (isRejected) {
    return (
      <ReviewPageShell loading={loading} setupBackPath={setupBackPath}>
        <Alert
          message="Your event was not approved"
          description={
            event.admin_remark ? (
              <div>
                <Text strong>Admin comment: </Text>
                <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
                  {event.admin_remark}
                </Paragraph>
              </div>
            ) : (
              "Please review and make the necessary changes, then resubmit."
            )
          }
          type="error"
          showIcon
        />

        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>

        <Flex gap={12} style={{ marginTop: 20, justifyContent: "flex-end" }}>
          <Button type="primary" loading={loading} onClick={submitForReview}>
            Resubmit for Review
          </Button>
        </Flex>
      </ReviewPageShell>
    );
  }

  if (event.status === "published") {
    return (
      <ReviewPageShell loading={loading} setupBackPath={setupBackPath}>
        <Alert
          message="Your event is live"
          description="Your event is published and visible to attendees. You can update it or reschedule."
          type="success"
          showIcon
        />
        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>
        {canUnpublish && (
          <Flex gap={12} style={{ marginTop: 20, justifyContent: "flex-end" }}>
            <Button loading={loading} onClick={unpublishEvent}>
              Unpublish
            </Button>
          </Flex>
        )}
      </ReviewPageShell>
    );
  }

  if (event.status === "scheduled") {
    return (
      <ReviewPageShell loading={loading} setupBackPath={setupBackPath}>
        <Alert
          message="Your event is scheduled"
          description="Your event will go live at the configured time. You can change the schedule or publish it immediately."
          type="warning"
          showIcon
        />
        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>
        {publishScheduleForm(
          status === "published" ? "Publish Now" : "Update Schedule",
        )}
      </ReviewPageShell>
    );
  }

  if (isApproved) {
    return (
      <ReviewPageShell loading={loading} setupBackPath={setupBackPath}>
        <Alert
          message="Your event has been approved!"
          description="Review the details below, then choose how you'd like to go live."
          type="success"
          showIcon
        />
        <div style={REVIEW_SUMMARY_SPACING}>
          <EventSetupReviewSummary event={event} />
        </div>
        {publishScheduleForm(
          status === "scheduled" ? "Schedule Event" : "Publish Event",
        )}
      </ReviewPageShell>
    );
  }

  return (
    <ReviewPageShell loading={loading} setupBackPath={setupBackPath}>
      <Alert
        message="Almost there!"
        description="Once you're happy with your event setup, submit it for admin approval. You'll be able to publish once it's been reviewed."
        type="info"
      />

      <div style={REVIEW_SUMMARY_SPACING}>
        <EventSetupReviewSummary event={event} />
      </div>

      <Flex gap={12} style={{ marginTop: 20, justifyContent: "flex-end" }}>
        <Button type="primary" loading={loading} onClick={submitForReview}>
          Submit for Review
        </Button>
      </Flex>
    </ReviewPageShell>
  );
};

export default ReviewAndPublish;
